import { ThemeEvents, VariantUpdateEvent } from "@theme/events";
import { formatCurrency } from "@theme/utilities";

class ProductBadge extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateBadge);
  }

  disconnectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateBadge);
  }

  /**
   * Updates the badge.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateBadge = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Cache hit or variant JSON: show the variant's badges now, the section HTML syncs them in the background.
    // A combined listing switch waits for the HTML (the custom badges belong to the other product).
    if (!event.detail.data.html) {
      if (!event.detail.data.newProduct && event.detail.resource) this.#renderVariantBadges(event.detail.resource);
      return;
    }

    // Find the correct product-badge in HTML by matching productId
    // This prevents taking badge from wrong product (e.g. recommendations)
    const productId = this.dataset.productId;
    let newBadge = null;

    if (productId) {
      // Find all product-badge elements and match by productId
      const allBadges = event.detail.data.html.querySelectorAll("product-badge");
      for (const badge of allBadges) {
        if (badge.dataset.productId === productId) {
          newBadge = badge.querySelector('[ref="badgesContainer"]');
          break;
        }
      }
    }

    // Fallback: if no productId match, use first badge (for single product pages)
    if (!newBadge) {
      const fallbackBadge = event.detail.data.html.querySelector("product-badge");
      newBadge = fallbackBadge?.querySelector('[ref="badgesContainer"]');
    }

    const currentBadge = this.querySelector('[ref="badgesContainer"]');

    if (!newBadge || !currentBadge) return;

    if (currentBadge.innerHTML !== newBadge.innerHTML) {
      currentBadge.replaceWith(newBadge);
    }
  };

  /**
   * Renders the sale / sold out badges from the variant data, like snippets/product-badges.liquid with `use_variant`.
   * @param {{ available?: boolean, price?: number, compare_at_price?: number | null }} variant
   */
  #renderVariantBadges(variant) {
    const container = this.querySelector('[ref="badgesContainer"]');
    if (!(container instanceof HTMLElement) || !container.hasAttribute("data-variant-badges")) return;

    const { showSaleBadge, showSoldOutBadge, saleBadgeType, onSaleText, saveText, soldOutText } = container.dataset;
    const template = container.querySelector("template[data-custom-badges]");
    /** @type {Node[]} */
    const badges = [];

    if (variant.available) {
      const price = Number(variant.price) || 0;
      const compareAtPrice = Number(variant.compare_at_price) || 0;

      if (showSaleBadge === "true" && compareAtPrice > price) {
        const badge = document.createElement("span");
        badge.className = "badge badge--sale";

        if (saleBadgeType === "percentage" || saleBadgeType === "fixed_amount") {
          const amount =
            saleBadgeType === "percentage"
              ? `${Math.floor(((compareAtPrice - price) * 100) / compareAtPrice)}%`
              : formatCurrency(compareAtPrice - price, window.FoxTheme?.moneyFormat || "${{amount}}");
          // Translation markup, rendered as HTML by Liquid too.
          badge.innerHTML = (saveText || "").replace("[amount]", amount);
        } else {
          badge.textContent = onSaleText || "";
        }

        badges.push(badge);
      }

      if (template instanceof HTMLTemplateElement) badges.push(template.content.cloneNode(true));
    } else if (showSoldOutBadge === "true") {
      const badge = document.createElement("span");
      badge.className = "badge badge--sold-out";
      badge.textContent = soldOutText || "";
      badges.push(badge);
    }

    container.replaceChildren(...badges);
    if (template) container.prepend(template);
    container.classList.toggle("product-badges--empty", !container.querySelector(".badge"));
  }
}

if (!customElements.get("product-badge")) {
  customElements.define("product-badge", ProductBadge);
}
